import { Component, Input, OnInit } from '@angular/core';

export interface ScaleConfig {
  min: number;
  max: number;
  greenRange: [number, number];
  yellowRange: [number, number];
  redRange: [number, number];
  unit: string;
  reverse?: boolean;
}

@Component({
  selector: 'app-sliding-scale',
  templateUrl: './sliding-scale.component.html',
  styleUrls: ['./sliding-scale.component.scss']
})
export class SlidingScaleComponent implements OnInit {
  @Input() value: number | string | null | undefined = null;
  @Input() displayText: string | null | undefined = null;
  @Input() showValue: boolean = true;
  @Input() size: 'small' | 'medium' | 'large' = 'medium';
  @Input() customWidth: string | null = null;
  @Input() config: ScaleConfig = {
    min: 0,
    max: 100,
    greenRange: [0, 70],
    yellowRange: [70, 85],
    redRange: [85, 100],
    unit: '',
    reverse: false
  };

  sliderWidth: number = 200;
  sliderHeight: number = 8;
  numericValue: number = 0;
  sliderPosition: number = 0;

  ngOnInit() {
    this.initializeSlider();
  }

  ngOnChanges() {
    this.initializeSlider();
  }

  private initializeSlider() {
    if (this.value !== null && this.value !== undefined && this.value !== '') {
      this.numericValue = typeof this.value === 'string' ? parseFloat(this.value) : this.value;
      const range = this.config.max - this.config.min;
      const position = Math.max(0, Math.min(100, ((this.numericValue - this.config.min) / range) * 100));
      this.sliderPosition = position;
    } else {
      this.numericValue = 0;
      this.sliderPosition = 0;
    }

    switch (this.size) {
      case 'small':
        this.sliderWidth = 150;
        this.sliderHeight = 6;
        break;
      case 'large':
        this.sliderWidth = 250;
        this.sliderHeight = 10;
        break;
      default:
        this.sliderWidth = 200;
        this.sliderHeight = 8;
    }
  }

  getSliderBackground(): string {
    const { min, max, greenRange, yellowRange, reverse } = this.config;

    const greenStartPercent = ((greenRange[0] - min) / (max - min)) * 100;
    const greenEndPercent = ((greenRange[1] - min) / (max - min)) * 100;
    const yellowEndPercent = ((yellowRange[1] - min) / (max - min)) * 100;

    if (reverse) {
      return `linear-gradient(to right, 
        #e57373 0%, 
        #ef9a9a ${(100 - yellowEndPercent) * 0.3}%, 
        #ffcc80 ${(100 - yellowEndPercent) * 0.7}%, 
        #fff176 ${100 - yellowEndPercent}%, 
        #81c784 ${100 - greenEndPercent}%, 
        #66bb6a ${100 - greenStartPercent * 0.7}%, 
        #388e3c 100%)`;
    } else {
      return `linear-gradient(to right, 
        #388e3c 0%, 
        #66bb6a ${greenStartPercent * 0.7}%, 
        #81c784 ${greenEndPercent}%, 
        #fff176 ${yellowEndPercent}%, 
        #ffcc80 ${yellowEndPercent + (100 - yellowEndPercent) * 0.3}%, 
        #ef9a9a ${yellowEndPercent + (100 - yellowEndPercent) * 0.7}%, 
        #e57373 100%)`;
    }
  }

  getMarkerColor(): string {
    const positionPercent = ((this.numericValue - this.config.min) / (this.config.max - this.config.min)) * 100;
    const { min, max, greenRange, yellowRange, reverse } = this.config;

    const greenStartPercent = ((greenRange[0] - min) / (max - min)) * 100;
    const greenEndPercent = ((greenRange[1] - min) / (max - min)) * 100;
    const yellowEndPercent = ((yellowRange[1] - min) / (max - min)) * 100;

    let gradientStops: { position: number; color: string }[];

    if (reverse) {
      gradientStops = [
        { position: 0, color: '#e57373' },
        { position: (100 - yellowEndPercent) * 0.3, color: '#ef9a9a' },
        { position: (100 - yellowEndPercent) * 0.7, color: '#ffcc80' },
        { position: 100 - yellowEndPercent, color: '#fff176' },
        { position: 100 - greenEndPercent, color: '#81c784' },
        { position: 100 - greenStartPercent * 0.7, color: '#66bb6a' },
        { position: 100, color: '#388e3c' }
      ];
    } else {
      gradientStops = [
        { position: 0, color: '#388e3c' },
        { position: greenStartPercent * 0.7, color: '#66bb6a' },
        { position: greenEndPercent, color: '#81c784' },
        { position: yellowEndPercent, color: '#fff176' },
        { position: yellowEndPercent + (100 - yellowEndPercent) * 0.3, color: '#ffcc80' },
        { position: yellowEndPercent + (100 - yellowEndPercent) * 0.7, color: '#ef9a9a' },
        { position: 100, color: '#e57373' }
      ];
    }

    return this.getColorFromGradient(gradientStops, positionPercent);
  }

  private getColorFromGradient(gradientStops: { position: number; color: string }[], positionPercent: number): string {
    let color1 = gradientStops[0];
    let color2 = gradientStops[gradientStops.length - 1];

    for (let i = 0; i < gradientStops.length - 1; i++) {
      if (positionPercent >= gradientStops[i].position && positionPercent <= gradientStops[i + 1].position) {
        color1 = gradientStops[i];
        color2 = gradientStops[i + 1];
        break;
      }
    }

    const ratio = (positionPercent - color1.position) / (color2.position - color1.position);
    const clampedRatio = Math.max(0, Math.min(1, ratio));
    return this.interpolateColor(color1.color, color2.color, clampedRatio);
  }

  private interpolateColor(color1: string, color2: string, ratio: number): string {
    const hex1 = color1.replace('#', '');
    const hex2 = color2.replace('#', '');

    const r1 = parseInt(hex1.substring(0, 2), 16);
    const g1 = parseInt(hex1.substring(2, 4), 16);
    const b1 = parseInt(hex1.substring(4, 6), 16);

    const r2 = parseInt(hex2.substring(0, 2), 16);
    const g2 = parseInt(hex2.substring(2, 4), 16);
    const b2 = parseInt(hex2.substring(4, 6), 16);

    const r = Math.round(r1 + (r2 - r1) * ratio);
    const g = Math.round(g1 + (g2 - g1) * ratio);
    const b = Math.round(b1 + (b2 - b1) * ratio);

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
  }

  getDisplayValue(): string {
    if (this.displayText) {
      return this.displayText;
    }

    if (this.config.unit) {
      return `${this.numericValue} ${this.config.unit}`;
    }
    return this.numericValue.toString();
  }

  getSeverityLevel(): 'low' | 'medium' | 'high' {
    const { greenRange, yellowRange, reverse } = this.config;

    const isInGreenRange = this.numericValue >= greenRange[0] && this.numericValue <= greenRange[1];
    const isInYellowRange = this.numericValue >= yellowRange[0] && this.numericValue <= yellowRange[1];

    if (isInGreenRange) return reverse ? 'high' : 'low';
    if (isInYellowRange) return 'medium';
    return reverse ? 'low' : 'high';
  }
}
